import { useEffect, useState } from 'react';
import { ThumbsUp, PenLine, AlertTriangle, HelpCircle } from 'lucide-react';
import PaneShell from './PaneShell';
import { MachineState, Rating, RATING_OPTIONS, buildSummary } from '../../../features/assistant/feedbackMachine';

const ICON: Record<Rating, typeof ThumbsUp> = { util: ThumbsUp, mejorable: PenLine, error: AlertTriangle, duda: HelpCircle };
const chip = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[13px]';
const chipOff = `${chip} bg-white border-slate-200 text-slate-700 hover:bg-slate-50`;
const chipOn = `${chip} border-indigo-300 bg-indigo-50 text-slate-900`;

interface Props {
  state: MachineState; hasTarget: boolean; optedOut: boolean; disabled?: boolean; topic: string | null; error: string | null;
  onRate: (r: Rating) => void; onAnswer: (ids: string[], text: string) => boolean; onConfirm: () => void;
  onCorrect: () => void; onSnooze: () => void; onOptOut: () => void; onOptIn: () => void; onComment: (t: string) => boolean | void | Promise<boolean | void>; onClose: () => void;
}

export default function AssistantPane(p: Props) {
  const { state } = p;
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const q = state.question;

  useEffect(() => { setSelected([]); setText(''); setInlineError(null); }, [q?.id, state.answers.length]);

  const submit = (ids: string[]) => {
    if (q && q.options.length === 0 && !text.trim()) { setInlineError('Escribe tu respuesta primero'); return; }
    if (q && q.options.length > 0 && ids.length === 0) { setInlineError('Elige una opción primero'); return; }
    p.onAnswer(ids, text.trim());
  };

  if (p.disabled) {
    return (
      <PaneShell title="Asistente" onClose={p.onClose} className="flex-1">
        <div className="flex-1 px-4 py-3 text-[13px] leading-relaxed">
          <p className="text-slate-600">El asistente no está disponible en este momento.</p>
        </div>
      </PaneShell>
    );
  }

  return (
    <PaneShell title="Asistente" onClose={p.onClose} className="flex-1">
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-[13px] leading-relaxed">
        {p.topic && <p className="text-[12px] text-slate-400">Sobre: {p.topic}</p>}

        {!p.hasTarget && (
          <p className="text-slate-600">Cuando el agente entregue una respuesta amplia, te preguntaré qué te pareció. También puedes comentar abajo.</p>
        )}

        {p.hasTarget && state.phase === 'asking' && (
          <div role="status">
            <p className="text-slate-800">¿Esta respuesta resolvió tu consulta?</p>
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {RATING_OPTIONS.map(o => {
                const Icon = ICON[o.id];
                return <button key={o.id} className={chipOff} onClick={() => p.onRate(o.id)}><Icon className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />{o.label}</button>;
              })}
            </div>
            <p className="mt-2.5 text-[11px] text-slate-400">
              <button className="hover:text-slate-600" onClick={p.onSnooze}>Hoy no</button>
              <span className="mx-1.5">·</span>
              <button className="hover:text-slate-600" onClick={p.onOptOut}>No volver a preguntar</button>
            </p>
          </div>
        )}

        {state.answers.map((a, i) => (
          <div key={i} className="space-y-1">
            <p className="text-slate-800">{a.pregunta}</p>
            <p className="text-right text-indigo-700">{a.respuesta}</p>
          </div>
        ))}

        {state.phase === 'clarifying' && q && (
          <div className="space-y-2.5">
            <p className="text-slate-800">{q.text}</p>
            {q.options.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {q.options.map(o => {
                  const on = selected.includes(o.id);
                  return (
                    <button key={o.id} className={on ? chipOn : chipOff} aria-pressed={on}
                      onClick={() => {
                        setInlineError(null);
                        if (q.multi || q.freeText) setSelected(s => (q.multi ? (on ? s.filter(x => x !== o.id) : [...s, o.id]) : [o.id]));
                        else p.onAnswer([o.id], '');
                      }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            )}
            {q.freeText && (
              <textarea value={text} onChange={e => { setText(e.target.value); setInlineError(null); }} rows={2} maxLength={2000}
                placeholder={q.options.length ? 'Agrega un detalle (opcional)' : 'Escribe aquí'}
                className="w-full rounded-lg bg-white border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            )}
            {(q.multi || q.freeText) && (
              <button className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-[13px] font-medium" onClick={() => submit(selected)}>Continuar</button>
            )}
            {inlineError && <p className="text-[13px] text-red-600">{inlineError}</p>}
          </div>
        )}

        {state.phase === 'confirming' && (
          <div className="pl-3 border-l-2 border-indigo-200">
            <p className="text-slate-800">{buildSummary(state)} ¿Es correcto?</p>
            <div className="flex gap-2 mt-2.5">
              <button className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-medium" onClick={p.onConfirm}>Sí, registrar</button>
              <button className="px-3 py-1.5 text-slate-500 hover:text-slate-700" onClick={p.onCorrect}>Corregir</button>
            </div>
          </div>
        )}

        {state.phase === 'registered' && (
          <p className="text-slate-600">
            {state.rating === 'util' ? 'Gracias por tu opinión.' : 'Gracias. Quedó registrado.'}
          </p>
        )}
        {state.phase === 'dismissed' && <p className="text-slate-600">Entendido.</p>}
        {p.optedOut && (
          <p className="text-[12px] text-slate-500">
            Las preguntas automáticas están desactivadas.{' '}
            <button className="text-indigo-600 hover:text-indigo-700 font-medium" onClick={p.onOptIn}>Activar</button>
          </p>
        )}
        {p.error && <p className="text-[13px] text-red-600">{p.error}</p>}
      </div>

      <form className="px-4 pb-4 flex-shrink-0" onSubmit={e => {
        e.preventDefault();
        if (!note.trim()) return;
        // El texto se conserva hasta que el guardado resuelva; solo se limpia si salió bien.
        Promise.resolve(p.onComment(note)).then(ok => { if (ok !== false) setNote(''); });
      }}>
        <input value={note} onChange={e => setNote(e.target.value)} disabled={!p.hasTarget} maxLength={2000}
          placeholder={p.hasTarget ? 'Comenta sobre esta respuesta…' : 'Sin respuesta que comentar todavía'}
          className="w-full rounded-lg bg-white border border-gray-200 px-3 py-2 text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50" />
        <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('openOnboarding'))}
          className="mt-1.5 px-1 text-[11px] text-slate-400 hover:text-slate-600">Guía de uso</button>
      </form>
    </PaneShell>
  );
}
